import { sqLicenseManagementStore } from '@/core/core.stores';
import { PUSH_IGNORE } from '@/core/flux.service';
import { Feature } from '@/licenseManagement/licenseManagement.constants';
import { hasValidFeatureInLicense } from '@/licenseManagement/licenseManagement.utilities';
import { isLockingAndVersioningEnabled } from '@/services/systemConfiguration.utilities';
import { isTemplateInformationInaccessible } from '@/tools/models/templateAccess.utilities';
import { getAllItems, isItemFromModelApplication } from '@/trend/trendDataHelper.utilities';
import { setTrendItemProps } from '@/trendData/trend.actions';
import { setColumnEnabled } from '@/trendData/trendCustomization.actions';
import { TREND_PANELS } from '@/trendData/trendData.constants';
import { errorToast } from '@/utilities/toast.utilities';
import { sqTemplatesApi } from '@seeqdev/api/endpoint/TemplatesApi';
import { SeeqNames } from '@seeqdev/constants';
import type { InstanceOutputV1 } from '@seeqdev/api/model/InstanceOutputV1';
import type { TemplateOutputV1 } from '@seeqdev/api/model/TemplateOutputV1';

export type StalenessProps = {
  isOnProductionVersion: boolean;
  versionNumber?: number;
  productionVersionNumber?: number;
  productionModelId?: string;
};

/** Model properties when only the instance id is known (e.g. other template fetch error). */
export type ModelItemPropsInstanceOnly = StalenessProps & {
  instanceId: string;
  modelId?: never;
  modelInformation?: never;
  modelName?: never;
  modelContainerId?: never;
  isModelInformationRedacted?: never;
};

/** Model properties when model metadata was loaded successfully. */
export type ModelItemPropsWithInformation = StalenessProps & {
  instanceId: string;
  modelId: string;
  modelInformation: string;
  modelName: string;
  modelContainerId?: string;
  isModelInformationRedacted?: false;
};

/** Model properties when model metadata cannot be read due to ACL. */
export type ModelItemPropsRedacted = StalenessProps & {
  instanceId: string;
  isModelInformationRedacted: true;
  modelId?: never;
  modelInformation?: never;
  modelName?: never;
  modelContainerId?: never;
};

export type ModelItemProps = ModelItemPropsInstanceOnly | ModelItemPropsWithInformation | ModelItemPropsRedacted;

/** Partial model props used while fetching or merging onto trend items. */
export type PartialModelItemProps = Partial<{
  instanceId: string;
  modelId: string;
  modelInformation: string;
  modelName: string;
  modelContainerId: string;
  isModelInformationRedacted: boolean;
}> &
  Partial<StalenessProps>;

/**
 * Computes version staleness by matching the instance's modelId against the version list.
 *
 * @param modelId - the Model GUID the instance was applied from
 * @param modelContainerId - the canonical ModelContainer ID
 * @returns staleness properties
 */
export async function computeStaleness(modelId: string, modelContainerId: string): Promise<StalenessProps> {
  try {
    const { data } = await sqTemplatesApi.getTemplateVersions({ templateId: modelContainerId });
    const versions = data.versions;

    if (versions.length === 0) {
      return { isOnProductionVersion: true };
    }

    const productionVersion = versions.find((v) => v.production);
    const instanceVersion = versions.find((v) => v.versionId === modelId);

    if (!productionVersion) {
      return { isOnProductionVersion: true };
    }

    return {
      isOnProductionVersion: instanceVersion?.versionId === productionVersion.versionId,
      versionNumber: instanceVersion?.version,
      productionVersionNumber: productionVersion.version,
      productionModelId: productionVersion.versionId,
    };
  } catch {
    // Locking/versioning not available — treat as on-production
    return { isOnProductionVersion: true };
  }
}

function getInstanceDisplayName(instanceInfo: InstanceOutputV1): string | undefined {
  if (instanceInfo.tableApplicationId) {
    return undefined;
  }
  const featureId =
    instanceInfo.inputFeatures?.find((input) => input.name === SeeqNames.Models.InstanceNameInputFeatureName)?.id ?? '';
  return instanceInfo.inputs?.find((input) => input.featureId === featureId)?.textInput;
}

/**
 * Extracts model properties from model and instance information
 *
 * @param modelInfo - the model information
 * @param instanceInfo - the model instance information
 * @param staleness - staleness properties computed from version metadata
 * @returns the model properties
 */
export function getPropsFromModelAndInstanceInfo(
  modelInfo: TemplateOutputV1,
  instanceInfo: InstanceOutputV1,
  staleness: StalenessProps = { isOnProductionVersion: true },
): ModelItemPropsWithInformation {
  const instanceName = getInstanceDisplayName(instanceInfo);
  // Same conditions as useLockingEnabled (hook cannot be used outside React components).
  const lockingEnabled =
    isLockingAndVersioningEnabled() &&
    hasValidFeatureInLicense(sqLicenseManagementStore.getState().license, Feature.Locking);
  const versionSuffix = lockingEnabled && staleness.versionNumber != null ? ` (v${staleness.versionNumber})` : '';
  const modelNameWithVersion = `${modelInfo.name}${versionSuffix}`;
  return {
    instanceId: instanceInfo.instanceId!,
    modelId: instanceInfo.templateId!,
    // Don't show the instance name when coming from a table application, since it is autogenerated
    modelInformation: instanceName ? `${modelNameWithVersion} | ${instanceName}` : modelNameWithVersion,
    modelName: modelInfo.name,
    modelContainerId: instanceInfo.templateContainerId ?? undefined,
    isModelInformationRedacted: false,
    ...staleness,
  };
}

export function getRedactedModelProps(instanceId: string): ModelItemPropsRedacted {
  return {
    instanceId,
    isModelInformationRedacted: true,
    isOnProductionVersion: true,
  };
}

const modelPropsInflight = new Map<string, Promise<PartialModelItemProps>>();

function fetchTemplatePropsOnce(instanceId: string): Promise<PartialModelItemProps> {
  let inflight = modelPropsInflight.get(instanceId);
  if (inflight) {
    return inflight;
  }

  inflight = (async () => {
    try {
      const { data: instanceInfo } = await sqTemplatesApi.getInstance({ instanceId });

      if (instanceInfo.isRetired) {
        return {};
      }

      const staleness = instanceInfo.templateContainerId
        ? await computeStaleness(instanceInfo.templateId!, instanceInfo.templateContainerId)
        : { isOnProductionVersion: true };

      const { data: modelInfo } = await sqTemplatesApi.getTemplate({ templateId: instanceInfo.templateId! });
      return getPropsFromModelAndInstanceInfo(modelInfo, instanceInfo, staleness);
    } catch (error) {
      if (isTemplateInformationInaccessible(error)) {
        return getRedactedModelProps(instanceId);
      }
      throw error;
    }
  })();

  modelPropsInflight.set(instanceId, inflight);
  inflight.catch(() => {}).finally(() => modelPropsInflight.delete(instanceId));
  return inflight;
}

export async function getTemplateProps(
  instanceId: string,
  setProps = false,
  itemId?: string,
): Promise<PartialModelItemProps> {
  try {
    const props = await fetchTemplatePropsOnce(instanceId);
    if (setProps && itemId && props.instanceId) {
      setTrendItemProps(itemId, props);
      if (props.isModelInformationRedacted) {
        setColumnEnabled(TREND_PANELS.SERIES, 'modelInformationColumn', true, PUSH_IGNORE);
      }
    }
    return props;
  } catch (error) {
    if (isTemplateInformationInaccessible(error)) {
      const props = getRedactedModelProps(instanceId);
      if (setProps && itemId) {
        setTrendItemProps(itemId, props);
        setColumnEnabled(TREND_PANELS.SERIES, 'modelInformationColumn', true, PUSH_IGNORE);
      }
      return props;
    }
    errorToast({ httpResponseOrError: error });
    const props = { instanceId };
    if (setProps && itemId) {
      setTrendItemProps(itemId, props);
    }
    return props;
  }
}

/**
 * Clears template related properties on the trend item.
 *
 * @param itemId - the id of the item containing an instanceId and a template name - instance name (if provided)
 * @returns void
 */
export function clearTemplateProps(itemId: string): void {
  setTrendItemProps(itemId, {
    instanceId: undefined,
    modelId: undefined,
    modelInformation: undefined,
    modelName: undefined,
    modelContainerId: undefined,
    isModelInformationRedacted: undefined,
    isOnProductionVersion: undefined,
    versionNumber: undefined,
    productionVersionNumber: undefined,
    productionModelId: undefined,
  });
}

/**
 * Re-fetches template instance information for all trend items that have a template instance
 * and clears the template info props if it has become retired.
 *
 * @param opts.onlyScalingItems - only items with scaling table context will be checked
 **/
export async function clearRetiredTemplatesProps(opts?: { onlyScalingItems?: boolean }): Promise<void> {
  const templateItems = getAllItems({ onlyScalingItems: opts?.onlyScalingItems }).filter(isItemFromModelApplication);
  const uniqueInstanceIds = Array.from(new Set(templateItems.map((item) => item.instanceId)));

  const instancesById: { [instanceId: string]: InstanceOutputV1 } = {};
  await Promise.allSettled(
    uniqueInstanceIds.map((instanceId) =>
      sqTemplatesApi.getInstance({ instanceId }).then(({ data: modelOutput }) => {
        instancesById[instanceId] = modelOutput;
      }),
    ),
  );

  templateItems.forEach((item) => {
    const instanceInfo = instancesById[item.instanceId];
    if (instanceInfo?.isRetired) {
      clearTemplateProps(item.id);
    }
  });
}
